import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { createTemplate } from '../../api/discord';
import type { DiscordWebhook, DiscordTemplate } from '../../types/discord';
import { TRIGGER_LABELS } from '../../types/discord';
import { extractVariables } from './templateUtils';

interface Props {
  open: boolean;
  webhooks: DiscordWebhook[];
  onClose: () => void;
  onCreated: (tpl: DiscordTemplate) => void;
}

export function AddTemplateModal({ open, webhooks, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('');
  const [webhookId, setWebhookId] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState('');
  const [active, setActive] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const variables = extractVariables(template);

  function reset() {
    setName('');
    setTrigger('');
    setWebhookId('');
    setDescription('');
    setTemplate('');
    setActive(true);
    setError('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      const tpl = await createTemplate({ name, trigger, webhookId, description, template, active });
      reset();
      onCreated(tpl);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Failed to create template');
    } finally {
      setSaving(false);
    }
  }

  const valid = name.trim() && trigger && webhookId && template.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Notification Template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Trigger</InputLabel>
            <Select value={trigger} label="Trigger" onChange={(e) => setTrigger(e.target.value)}>
              {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Webhook</InputLabel>
            <Select
              value={webhookId}
              label="Webhook"
              onChange={(e) => setWebhookId(e.target.value)}
            >
              {webhooks.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name} · {w.channel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
          />
          <TextField
            label="Message template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            multiline
            minRows={3}
            size="small"
            helperText="Use {{variable_name}} placeholders"
          />
          {variables.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Variables detected:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {variables.map((v) => (
                  <Chip key={v} label={`{{${v}}}`} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active"
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || saving}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
